import { Rule } from '@/rules';
import { TurnishOptions } from '@/index';
import { isCodeBlock, repeat, RequireOnly, sanitizedLinkContent, sanitizedLinkTitle, splitBlockEdges, trimNewlines, wrapInlineContent } from '@/utilities';
import { isList, isListItemWrapper, isNestedListWrapper, isTrailingNestedList, renderListItem } from '@/list';

export const defaultRules: { [key: string]: Rule } = {}

defaultRules.paragraph = {
  filter: function (node: any): boolean {
    return node.nodeName === 'P' && node.isBlock;
  },
  replacement: function (content: string): string {
    return '\n\n' + content + '\n\n';
  }
};

defaultRules.lineBreak = {
  filter: 'br',
  replacement: function (_content: string, _node: Node, options: TurnishOptions): string {
    return options.br + '\n';
  }
};

defaultRules.heading = {
  filter: function (node: any): boolean {
    return ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.nodeName) && node.isBlock;
  },
  replacement: function (content: string, node: Node, options: TurnishOptions): string {
    const hLevel = Number(node.nodeName.charAt(1));
    if (options.headingStyle === 'setext' && hLevel < 3) {
      const underline = repeat((hLevel === 1 ? '=' : '-'), content.length);
      return (
        '\n\n' + content + '\n' + underline + '\n\n'
      );
    } else {
      return '\n\n' + repeat('#', hLevel) + ' ' + content + '\n\n';
    }
  }
};

defaultRules.blockquote = {
  filter: function (node: any): boolean {
    return node.nodeName === 'BLOCKQUOTE' && node.isBlock;
  },
  replacement: function (content: string): string {
    content = trimNewlines(content).replace(/^/gm, '> ');
    return '\n\n' + content + '\n\n';
  }
};

defaultRules.listWrapper = {
  filter: function (node: any): boolean {
    return isNestedListWrapper(node) || isListItemWrapper(node);
  },
  replacement: function (content: string): string {
    return content;
  }
};

defaultRules.list = {
  filter: function (node: any): boolean {
    return isList(node) && node.isBlock;
  },
  replacement: function (content: string, node: Node): string {
    return isTrailingNestedList(node) ? '\n' + content : '\n\n' + content + '\n\n';
  }
};

defaultRules.listItem = {
  filter: function (node: any): boolean {
    return node.nodeName === 'LI' && node.isBlock;
  },
  replacement: function (content: string, node: Node, options: TurnishOptions): string {
    return renderListItem(content, node as Element, options);
  }
};

defaultRules.indentedCodeBlock = {
  filter: function (node: Node, options: TurnishOptions): boolean {
    return !!(
      options &&
      options.codeBlockStyle === 'indented' &&
      node.nodeName === 'PRE' &&
      node.firstChild &&
      (node.firstChild as Element).nodeName === 'CODE'
    );
  },
  replacement: function (_content: string, node: Node): string {
    if (!node || !node.firstChild) return '';
    return (
      '\n\n    ' +
      (node.firstChild as Element).textContent!.replace(/\n/g, '\n    ') +
      '\n\n'
    );
  }
};

defaultRules.fencedCodeBlock = {
  filter: function (node: Node, options: TurnishOptions): boolean {
    return !!(
      options &&
      options.codeBlockStyle === 'fenced' &&
      node.nodeName === 'PRE'
    );
  },
  replacement: function (_content: string, node: Node, options: TurnishOptions): string {
    const preNode = node as Element;
    const firstChild = preNode.firstChild;
    const codeElem = (firstChild && firstChild.nodeName === 'CODE') ? firstChild as Element : preNode;

    const className = codeElem.getAttribute('class') || '';
    const language = (className.match(/(?:lang|language)-(\S+)/) || [null, ''])[1];
    const code = codeElem.textContent || '';
    const fenceChar = options.fence?.charAt(0) || '`';
    let fenceSize = 3;
    const fenceInCodeRegex = new RegExp('^' + fenceChar + '{3,}', 'gm');
    let match;
    while ((match = fenceInCodeRegex.exec(code))) {
      if (match[0].length >= fenceSize) {
        fenceSize = match[0].length + 1;
      }
    }
    const fence = repeat(fenceChar, fenceSize);
    return (
      '\n\n' + fence + language + '\n' +
      code.replace(/\n$/, '') +
      '\n' + fence + '\n\n'
    );
  }
};

defaultRules.horizontalRule = {
  filter: 'hr',
  replacement: function (_content: string, _node: Node, options: TurnishOptions): string {
    return '\n\n' + options.hr + '\n\n';
  }
};

defaultRules.inlineLink = {
  filter: function (node: Node, options: TurnishOptions): boolean {
    return !!(
      options?.linkStyle === 'inlined' &&
      node.nodeName === 'A' &&
      (node as Element).getAttribute('href')
    );
  },
  replacement: function (content: string, node: Node): string {
    const { leading, body, trailing } = splitBlockEdges(content);
    const sanitizedContent = sanitizedLinkContent(body);
    let href = (node as Element)
      .getAttribute('href')
      ?.replace(/([()])/g, '\\$1');
    let title: string;
    const titleAttr = (node as Element).getAttribute('title');
    if (titleAttr) {
      const sanitizedTitle = sanitizedLinkTitle(titleAttr);
      title = ' "' + sanitizedTitle.replace(/"/g, '\\"') + '"';
    } else {
      title = '';
    }
    return leading + '[' + sanitizedContent + '](' + href + title + ')' + trailing;
  }
};

const referenceLinkRule: RequireOnly<Rule, "urlReferenceIdMap" | "references"> = {
  filter: function (node: Node, options: TurnishOptions): boolean {
    return !!(
      options &&
      options.linkStyle === 'referenced' &&
      node.nodeName === 'A' &&
      (node as Element).getAttribute('href')
    );
  },
  replacement: function (content: string, node: Node, options: TurnishOptions): string {
    const self = referenceLinkRule;

    const { leading, body, trailing } = splitBlockEdges(content);
    content = sanitizedLinkContent(body);
    const href = (node as Element).getAttribute('href');
    let title: string;
    const titleAttr = (node as Element).getAttribute('title');
    if (titleAttr) {
      const sanitizedTitle = sanitizedLinkTitle(titleAttr);
      title = ' "' + sanitizedTitle + '"';
    } else {
      title = '';
    }
    const referenceKey = href + title;

    let replacement: string;
    let reference: string;
    switch (options.linkReferenceStyle) {
      case 'collapsed':
        replacement = '[' + content + '][]';
        reference = '[' + content + ']: ' + referenceKey;
        break;
      case 'shortcut':
        replacement = '[' + content + ']';
        reference = '[' + content + ']: ' + referenceKey;
        break;
      default: {
        let id: number;
        const existingKey = self.urlReferenceIdMap.get(referenceKey);
        if (options.linkReferenceDeduplication === 'full' && existingKey) {
          id = existingKey;
          reference = '[' + id + ']: ' + href + title;
        } else {
          id = self.references.length + 1;
          self.urlReferenceIdMap.set(referenceKey, id);
          reference = '[' + id + ']: ' + href + title;
          self.references.push(reference);
        }
        replacement = '[' + content + '][' + id + ']';
        break;
      }
    }

    if (options.linkReferenceStyle !== 'full') {
      // Check if we should deduplicate
      if (options.linkReferenceDeduplication === 'full') {
        if (!self.urlReferenceIdMap.has(referenceKey)) {
          self.urlReferenceIdMap.set(referenceKey, 1);
          self.references.push(reference);
        }
      } else {
        self.references.push(reference);
      }
    }
    return leading + replacement + trailing;
  },
  references: [],
  urlReferenceIdMap: new Map<string, number>(),
  append: (): string => {
    const self = referenceLinkRule;
    let references = '';
    if (self.references && self.references.length) {
      references = '\n\n' + self.references.join('\n') + '\n\n';
      self.references = [];
      self.urlReferenceIdMap = new Map();
    }
    return references;
  }
};

defaultRules.referenceLink = referenceLinkRule;

defaultRules.emphasis = {
  filter: ['em', 'i'],
  replacement: (content: string, _node: Node, options: TurnishOptions): string => {
    return wrapInlineContent(content, text => options.emDelimiter + text + options.emDelimiter);
  }
};

defaultRules.strong = {
  filter: ['strong', 'b'],
  replacement: (content: string, _node: Node, options: TurnishOptions): string => {
    return wrapInlineContent(content, text => options.strongDelimiter + text + options.strongDelimiter);
  }
};

defaultRules.code = {
  filter: (node: Node): boolean => {
    const parent = node.parentNode as Element;
    const isCodeBlockNode = isCodeBlock(parent);
    return node.nodeName === 'CODE' && !isCodeBlockNode;
  },
  replacement: (content: string, _node: Node, options: TurnishOptions): string => {
    const trimmed = options.preformattedCode
      ? content.replace(/\r?\n|\r/g, ' ')
      : content.trim().replace(/[ \t]*(?:\r?\n|\r)\s*/g, ' ');
    const extraSpace = /^`|^ .*?[^ ].* $|`$/.test(trimmed) ? ' ' : '';
    let delimiter = '`';
    const matches: string[] = trimmed.match(/`+/gm) || [];
    while (matches.includes(delimiter)) delimiter = delimiter + '`';
    return delimiter + extraSpace + trimmed + extraSpace + delimiter;
  }
};

defaultRules.image = {
  filter: 'img',
  replacement: function (_content: string, node: Node): string {
    const altAttr = (node as Element).getAttribute('alt');
    const alt = altAttr ? sanitizedLinkTitle(altAttr) : '';
    const src = (node as Element).getAttribute('src') || '';
    const titleAttr = (node as Element).getAttribute('title');
    const title = titleAttr ? sanitizedLinkTitle(titleAttr) : '';
    const titlePart = title ? ' "' + title + '"' : '';
    return src ? '![' + alt + ']' + '(' + src + titlePart + ')' : '';
  }
};
